/**
 * Tab strip rules shared by every tab host — the main window's strip
 * (components/popout/useMainWindowTabs.ts) and a tabbed pop-out window
 * (components/popout/AppTabsHost.tsx) — so both reorder, pin, take keyboard
 * shortcuts and handle drag-and-drop the same way. Pure functions only; the
 * hosts own their state.
 *
 * ORDER. A strip is two groups, always in this order:
 *   pinned   compact tabs the user pinned. Stay left; move only among themselves.
 *   normal   everything else.
 * A move is clamped to the moving tab's own group, so no reorder (drag, menu,
 * keyboard) can put a normal tab before a pinned one. When nothing is pinned
 * the order is free. The main window's primary (home) tab follows the same
 * rules as any other tab: it can be reordered, pinned and unpinned (its host
 * only stops it from being closed or moved to another window).
 */
import { parseAppTabKey } from './popout';

export interface OrderedTab {
  uid: string;
  pinned?: boolean;
}

/** Pinned first, then the rest — each group keeping its order. */
export function normalizeOrder<T extends OrderedTab>(list: T[]): T[] {
  const pinned = list.filter((t) => t.pinned);
  const rest = list.filter((t) => !t.pinned);
  const out = [...pinned, ...rest];
  return out.every((t, i) => t === list[i]) ? list : out;
}

/** The indexes a tab may occupy (inclusive), or null when there is no such tab. */
export function moveRange<T extends OrderedTab>(list: T[], uid: string): [number, number] | null {
  const tab = list.find((t) => t.uid === uid);
  if (!tab) return null;
  const rest = list.filter((t) => t.uid !== uid);
  const pinnedLeft = rest.filter((t) => t.pinned).length;
  return tab.pinned ? [0, pinnedLeft] : [pinnedLeft, rest.length];
}

/**
 * Move a tab so it ends up at `index` (clamped to its group). Returns the same
 * array when nothing changes, so a no-op move never re-renders or re-saves.
 */
export function moveTabTo<T extends OrderedTab>(list: T[], uid: string, index: number): T[] {
  const range = moveRange(list, uid);
  if (!range) return list;
  const from = list.findIndex((t) => t.uid === uid);
  const at = Math.max(range[0], Math.min(range[1], Math.round(index)));
  if (at === from) return list;
  const next = list.filter((t) => t.uid !== uid);
  next.splice(at, 0, list[from]);
  return next;
}

export function moveTabBy<T extends OrderedTab>(list: T[], uid: string, delta: number): T[] {
  const from = list.findIndex((t) => t.uid === uid);
  return from < 0 ? list : moveTabTo(list, uid, from + delta);
}

export function moveTabToEdge<T extends OrderedTab>(list: T[], uid: string, edge: 'start' | 'end'): T[] {
  return moveTabTo(list, uid, edge === 'start' ? 0 : list.length);
}

/** Can this tab move one step (or to the edge) in that direction? For menu items' disabled state. */
export function canMoveTab<T extends OrderedTab>(list: T[], uid: string, direction: -1 | 1): boolean {
  const range = moveRange(list, uid);
  if (!range) return false;
  const from = list.findIndex((t) => t.uid === uid);
  return direction < 0 ? from > range[0] : from < range[1];
}

/**
 * Pin or unpin. Both land the tab on the boundary between the pinned and normal
 * groups (pinning: last pinned tab; unpinning: first normal tab), like a browser.
 */
export function setTabPinned<T extends OrderedTab>(list: T[], uid: string, pinned: boolean): T[] {
  const tab = list.find((t) => t.uid === uid);
  if (!tab || Boolean(tab.pinned) === pinned) return list;
  const rest = list.filter((t) => t.uid !== uid);
  const at = rest.filter((t) => t.pinned).length;
  rest.splice(at, 0, { ...tab, pinned });
  return rest;
}

/** How many tabs are pinned (they are the ones at the front). */
export function pinnedCount(list: OrderedTab[]): number {
  return list.filter((t) => t.pinned).length;
}

/** Mark the first `count` tabs pinned — how a saved count / `pinned=` param is restored. */
export function applyPinnedCount<T extends OrderedTab>(list: T[], count: number): T[] {
  let left = Number.isInteger(count) && count > 0 ? count : 0;
  return list.map((t) => {
    const pinned = left > 0;
    if (pinned) left--;
    return Boolean(t.pinned) === pinned ? t : { ...t, pinned };
  });
}

// ---------------------------------------------------------------- drag and drop
/** MIME type a dragged tab carries, so any WeaveIn strip (in any window) can take it. */
export const TAB_DRAG_MIME = 'application/x-weavein-tab';
/**
 * Set on EVERY tab drag, even one that cannot leave its window (the main
 * window's primary tab carries no TAB_DRAG_MIME payload). Firefox does not
 * start a drag whose dragstart sets no data at all. Other windows ignore it.
 */
export const TAB_LOCAL_DRAG_MIME = 'application/x-weavein-tab-local';
/** Dropped this far below the strip (or outside the window) = "tear the tab off into a new window". */
export const DRAG_OUT_PX = 80;
/** Within this many px of the strip's scroll edge while dragging, the strip scrolls. */
export const AUTO_SCROLL_EDGE_PX = 32;
export const AUTO_SCROLL_STEP_PX = 14;

export interface TabDragPayload {
  v: 1;
  tabKey: string;
  title?: string;
  /** Window the tab is being dragged out of (lib/tabMove windowId). */
  sourceWindowId: string;
  /** One per drag; the source only lets go of the tab for an ack carrying it. */
  nonce: string;
}

const WINDOW_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const NONCE_RE = /^[a-z0-9]{8,40}$/;

export function isValidWindowId(id: unknown): id is string {
  return typeof id === 'string' && WINDOW_ID_RE.test(id);
}

export function isValidNonce(n: unknown): n is string {
  return typeof n === 'string' && NONCE_RE.test(n);
}

export function encodeTabDragPayload(p: TabDragPayload): string {
  return JSON.stringify(p);
}

/** A drag payload we could have built, or null (another app's drag, a forged one, garbage). */
export function parseTabDragPayload(raw: unknown): TabDragPayload | null {
  if (typeof raw !== 'string' || !raw || raw.length > 2048) return null;
  let d: Record<string, unknown>;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    d = parsed as Record<string, unknown>;
  } catch {
    return null;
  }
  if (d.v !== 1 || !parseAppTabKey(d.tabKey) || !isValidWindowId(d.sourceWindowId) || !isValidNonce(d.nonce)) return null;
  const out: TabDragPayload = { v: 1, tabKey: d.tabKey as string, sourceWindowId: d.sourceWindowId, nonce: d.nonce };
  if (typeof d.title === 'string' && d.title) out.title = d.title.slice(0, 120);
  return out;
}

/** Does this drag carry a WeaveIn tab? (Types are readable during dragover; data is not.) */
export function dragCarriesTab(types: ReadonlyArray<string> | DOMStringList | null | undefined): boolean {
  if (!types) return false;
  return Array.from(types as ArrayLike<string>).includes(TAB_DRAG_MIME);
}

/**
 * Where a drop lands, as a gap index 0…n (0 = before the first tab). The
 * pointer counts as "before" a tab up to and including its midpoint.
 */
export function dropGapFromPointer(rects: ReadonlyArray<{ left: number; width: number }>, clientX: number): number {
  for (let i = 0; i < rects.length; i++) {
    if (clientX <= rects[i].left + rects[i].width / 2) return i;
  }
  return rects.length;
}

/** A gap index → the final index of a tab moved from `from` (removing it first shifts later gaps left). */
export function gapToIndex(from: number, gap: number): number {
  return from >= 0 && gap > from ? gap - 1 : gap;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * The drag's end point in this window's client coordinates. Firefox reports
 * clientX/Y as 0 on dragend; the screen position still holds, so fall back to it.
 */
export function dragEndPoint(
  e: { clientX: number; clientY: number; screenX: number; screenY: number },
  win: { screenX: number; screenY: number; outerHeight: number; innerHeight: number },
): Point {
  if ((e.clientX !== 0 || e.clientY !== 0) || (e.screenX === 0 && e.screenY === 0)) return { x: e.clientX, y: e.clientY };
  const chrome = Math.max(0, win.outerHeight - win.innerHeight);
  return { x: e.screenX - win.screenX, y: e.screenY - win.screenY - chrome };
}

/**
 * Was the tab dropped "off" the strip — outside the window, or well below the
 * strip — so it should tear off into its own window?
 */
export function isDragOut(point: Point, strip: { bottom: number }, viewport: { width: number; height: number }): boolean {
  if (point.x < 0 || point.y < 0 || point.x > viewport.width || point.y > viewport.height) return true;
  return point.y > strip.bottom + DRAG_OUT_PX;
}

/** Auto-scroll step for a strip while dragging near its edges (negative = left). */
export function autoScrollDelta(clientX: number, box: { left: number; right: number }): number {
  if (clientX < box.left + AUTO_SCROLL_EDGE_PX) return -AUTO_SCROLL_STEP_PX;
  if (clientX > box.right - AUTO_SCROLL_EDGE_PX) return AUTO_SCROLL_STEP_PX;
  return 0;
}

// ---------------------------------------------------------------- keyboard
/**
 * Strip shortcuts, as the key a host acts on:
 *   '1'…'9'      Ctrl/Cmd+digit — jump to a tab (9 = last)
 *   'w'          Ctrl/Cmd+W — close the tab (hosts honour it on desktop only)
 *   'move-left'  Ctrl/Cmd+Shift+PageUp, or Alt+Shift+←
 *   'move-right' Ctrl/Cmd+Shift+PageDown, or Alt+Shift+→
 *
 * Ctrl+Shift+PageUp/PageDown is the browsers' own "move tab" key, so on the
 * web Chrome and Firefox usually keep it and the page never sees it (it is
 * honoured when it does arrive, and always on the desktop app). Alt+Shift+←/→
 * is free in every browser and in GlobalShortcuts, but on macOS it also
 * extends a text selection by a word, so it is ignored while typing.
 */
export type TabShortcut = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'w' | 'move-left' | 'move-right';

export const TAB_SHORTCUT_KEYS: ReadonlySet<string> = new Set([
  '1', '2', '3', '4', '5', '6', '7', '8', '9', 'w', 'move-left', 'move-right',
]);

export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el !== 'object' || !('tagName' in el)) return false;
  const tag = String(el.tagName).toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(el.isContentEditable);
}

export function tabShortcutFromEvent(e: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  target?: EventTarget | null;
}): TabShortcut | null {
  const mod = e.metaKey || e.ctrlKey;
  if (mod && e.shiftKey && !e.altKey) {
    if (e.key === 'PageUp') return 'move-left';
    if (e.key === 'PageDown') return 'move-right';
    return null;
  }
  if (!mod && e.altKey && e.shiftKey) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return null;
    if (isEditableTarget(e.target ?? null)) return null;
    return e.key === 'ArrowLeft' ? 'move-left' : 'move-right';
  }
  if (mod && !e.altKey && !e.shiftKey) {
    const key = e.key.toLowerCase();
    if (/^[1-9]$/.test(key) || key === 'w') return key as TabShortcut;
  }
  return null;
}
